// web/src/screens/Settings.tsx (/settings) — profile (from me), workspace, your preferences + sale alerts per site
// (portal spec 14), business settings (CONTRACT Settings: businessName / contactEmail / notifyEmail), connections,
// Log out (D06 A13, D14 §1).
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { logout } from '../lib/auth';
import type { Settings as SettingsDto, Site } from '../lib/types';
import { PreferencesCard } from '../components/PreferencesCard';
import { PageHeader } from '../components/PageHeader';
import { AsyncBoundary } from '../components/AsyncBoundary';
import { Skeleton } from '../components/Skeleton';
import { FormField } from '../components/FormField';
import { Icon } from '../components/Icon';
import { toast } from '../components/Toast';
import { ACCENTS, readMode, readAccent, setMode, setAccent, type Mode } from '../lib/theme';

export function Settings() {
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.me() });
  const q = useQuery({ queryKey: ['settings'], queryFn: () => api.getSettings() });

  return (
    <>
      <PageHeader title="Settings" subtitle="Your profile, workspace, and notifications." />
      <AsyncBoundary query={q} skeleton={<Skeleton variant="card" count={3} />}>
        {(s) => <SettingsForm initial={s} meName={me.data?.name} meEmail={me.data?.email} orgName={me.data?.orgName} role={me.data?.role} />}
      </AsyncBoundary>
    </>
  );
}

function SettingsForm({ initial, meName, meEmail, orgName, role }: {
  initial: SettingsDto; meName?: string; meEmail?: string; orgName?: string; role?: string;
}) {
  const [businessName, setBusinessName] = useState(initial.businessName);
  const [contactEmail, setContactEmail] = useState(initial.contactEmail);
  const [notifyEmail, setNotifyEmail] = useState(initial.notifyEmail);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => { setDirty(
    businessName !== initial.businessName || contactEmail !== initial.contactEmail || notifyEmail !== initial.notifyEmail,
  ); }, [businessName, contactEmail, notifyEmail, initial]);

  async function save() {
    setBusy(true);
    await api.saveSettings({ businessName: businessName.trim(), contactEmail: contactEmail.trim(), notifyEmail });
    setBusy(false); setDirty(false);
    toast({ kind: 'success', message: 'Settings saved' });
  }

  return (
    <div className="flex flex-col gap-6">
      <AppearanceCard />

      <Card title="Profile">
        <Row label="Name" value={meName ?? '—'} />
        <Row label="Sign-in email" value={meEmail ?? '—'} />
      </Card>

      <Card title="Workspace">
        <Row label="Workspace" value={orgName ?? '—'} />
        <Row label="Your role" value={<span className="font-head text-xs uppercase text-info">{role ?? '—'}</span>} />
      </Card>

      <SiteSettings />

      <section className="rounded-lg border border-hair bg-surface p-4">
        <h2 className="mb-3 font-head text-sm text-muted">Business details</h2>
        <div className="flex flex-col gap-4">
          <FormField label="Business name" name="biz-name" value={businessName} onChange={setBusinessName} />
          <FormField label="Contact email" name="biz-email" type="email" inputMode="email" value={contactEmail} onChange={setContactEmail}
            validate={(v) => (v && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? 'Enter a valid email' : null)} />
          <Toggle label="Email me when a request needs my action or ships" on={notifyEmail} onToggle={() => setNotifyEmail((v) => !v)} />
          <button onClick={save} disabled={busy || !dirty}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-run px-4 font-medium text-runink transition-transform active:scale-[0.98] disabled:opacity-50">
            <Icon name="check" size={16} />{busy ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </section>

      <Card title="Connections">
        <div className="flex items-center justify-between py-2">
          <span className="flex items-center gap-2 text-sm text-ink"><Icon name="git" size={16} />GitHub</span>
          <span className="inline-flex items-center gap-1 text-xs text-run"><Icon name="check" size={14} />Connected</span>
        </div>
      </Card>

      <button onClick={() => logout()}
        className="inline-flex min-h-11 items-center justify-center gap-2 self-start rounded-md border border-hair px-4 text-sm text-ink">
        <Icon name="logout" size={16} />Log out
      </button>
    </div>
  );
}

// ── Per-site: your preferences (portal spec 14 / G2, A4) and sale alerts (moved here from the old go-live card) ──
function SitePicker({ sites, value, onChange, label }: { sites: Site[]; value: string; onChange: (id: string) => void; label: string }) {
  if (sites.length < 2) return null;
  return (
    <label className="mb-3 block text-sm text-muted">{label}
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="mt-1 block min-h-11 w-full min-w-0 rounded-md border border-hair bg-bg px-3 text-ink">
        {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
      </select>
    </label>
  );
}

function SiteSettings() {
  const q = useQuery({ queryKey: ['sites'], queryFn: () => api.listSites() });
  const sites = (q.data ?? []).filter((s) => s.stage !== 'demo');
  const paying = sites.filter((s) => s.takesPayments);
  const [prefId, setPrefId] = useState('');
  const [alertId, setAlertId] = useState('');
  const pref = sites.find((s) => s.id === prefId) ?? sites[0];
  const alert = paying.find((s) => s.id === alertId) ?? paying[0];
  if (!sites.length) return null;
  return (
    <>
      <section className="flex flex-col gap-2">
        <h2 className="font-head text-sm text-muted">Your preferences</h2>
        <SitePicker sites={sites} value={pref!.id} onChange={setPrefId} label="Which website?" />
        <PreferencesCard key={pref!.id} siteId={pref!.id} />
      </section>
      {alert && <SaleAlertsCard key={alert.id} sites={paying} site={alert} onPick={setAlertId} />}
    </>
  );
}

function SaleAlertsCard({ sites, site, onPick }: { sites: Site[]; site: Site; onPick: (id: string) => void }) {
  const [channel, setChannel] = useState<'email' | 'sms' | 'both'>('email');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<unknown>, ok: string, bad: string) => {
    setBusy(true);
    try { await fn(); toast({ kind: 'success', message: ok }); }
    catch { toast({ kind: 'error', message: bad }); }
    finally { setBusy(false); }
  };
  const save = () => run(() => api.setAlerts(site.id, channel, channel !== 'sms' ? email.trim() || undefined : undefined, channel !== 'email' ? phone.replace(/[\s()-]/g, '') || undefined : undefined),
    'Saved. We’ll tell you here when you make a sale.', 'Couldn’t save. Check the email or phone number (like +18015551234).');
  const labels = { email: 'Email', sms: 'Text', both: 'Both' } as const;
  return (
    <section className="rounded-lg border border-hair bg-surface p-4 text-sm">
      <h2 className="mb-3 font-head text-sm text-muted">Sale alerts</h2>
      <SitePicker sites={sites} value={site.id} onChange={onPick} label="Which website?" />
      <p className="mb-2 text-muted">Where should we send sale alerts?</p>
      <div className="mb-3 flex gap-2">
        {(['email', 'sms', 'both'] as const).map((ch) => (
          <button key={ch} type="button" aria-pressed={channel === ch} onClick={() => setChannel(ch)}
            className={`min-h-11 flex-1 rounded-md border px-2 text-sm ${channel === ch ? 'border-run bg-run text-runink' : 'border-hair text-muted'}`}>{labels[ch]}</button>
        ))}
      </div>
      {channel !== 'sms' && <input value={email} onChange={(e) => setEmail(e.target.value)} inputMode="email" autoCapitalize="none" aria-label="Email for sale alerts"
        placeholder="you@business.com" className="mb-2 block min-h-11 w-full min-w-0 rounded-md border border-hair bg-bg px-3 text-ink" />}
      {channel !== 'email' && <input value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" aria-label="Phone for sale alerts"
        placeholder="+1 801 555 0123" className="mb-2 block min-h-11 w-full min-w-0 rounded-md border border-hair bg-bg px-3 text-ink" />}
      <div className="flex flex-col gap-2 sm:flex-row">
        <button onClick={save} disabled={busy}
          className="inline-flex min-h-11 flex-1 items-center justify-center rounded-md bg-run px-4 font-medium text-runink disabled:opacity-50">Save</button>
        <button onClick={() => run(() => api.testAlert(site.id), 'Test alert sent. Check your inbox or phone.', 'Couldn’t send a test. Save where alerts go first.')} disabled={busy}
          className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-md border border-hair px-4 text-sm text-ink disabled:opacity-50">
          <Icon name="message" size={15} />Send test
        </button>
      </div>
    </section>
  );
}

function AppearanceCard() {
  const [mode, setModeS] = useState<Mode>(readMode());
  const [accent, setAccentS] = useState(readAccent());
  const modes: { id: Mode; label: string; icon: string }[] = [
    { id: 'light', label: 'Light', icon: 'sun' },
    { id: 'dark', label: 'Dark', icon: 'moon' },
    { id: 'system', label: 'System', icon: 'monitor' },
  ];
  return (
    <section className="rounded-lg border border-hair bg-surface p-4">
      <h2 className="mb-3 font-head text-sm text-muted">Appearance</h2>
      <div className="flex flex-col gap-5">
        <div>
          <span className="mb-2 block text-sm text-ink">Theme</span>
          <div className="inline-flex rounded-md border border-hair p-1">
            {modes.map((m) => (
              <button key={m.id} type="button" aria-pressed={mode === m.id}
                onClick={() => { setMode(m.id); setModeS(m.id); }}
                className={`inline-flex min-h-9 items-center gap-1.5 rounded px-3 text-sm transition-colors ${mode === m.id ? 'bg-run text-runink' : 'text-muted hover:text-ink'}`}>
                <Icon name={m.icon} size={15} />{m.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <span className="mb-2 block text-sm text-ink">Color</span>
          <div className="flex flex-wrap items-center gap-3">
            {ACCENTS.map((a) => (
              <button key={a.id} type="button" aria-label={a.label} title={a.label} aria-pressed={accent === a.id}
                onClick={() => { setAccent(a.id); setAccentS(a.id); }}
                className={`size-8 rounded-full ring-2 ring-offset-2 ring-offset-surface transition-transform active:scale-95 ${accent === a.id ? 'ring-ink' : 'ring-transparent hover:ring-hair'}`}
                style={{ background: a.swatch }} />
            ))}
          </div>
          <span className="mt-2 block text-xs text-muted">Saved on this device.</span>
        </div>
      </div>
    </section>
  );
}

function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="rounded-lg border border-hair bg-surface p-4">
      <h2 className="mb-3 font-head text-sm text-muted">{title}</h2>
      <div className="divide-y divide-hair">{children}</div>
    </section>
  );
}
function Row({ label, value }: { label: string; value: ReactNode }) {
  return <div className="flex items-center justify-between py-2 text-sm"><span className="text-muted">{label}</span><span className="text-ink">{value}</span></div>;
}
function Toggle({ label, on, onToggle }: { label: string; on: boolean; onToggle(): void }) {
  return (
    <div className="flex items-center justify-between py-1">
      <span className="pr-3 text-sm text-ink">{label}</span>
      <button role="switch" aria-checked={on} onClick={onToggle}
        className={`relative h-6 w-11 shrink-0 rounded-pill transition-colors ${on ? 'bg-run' : 'bg-surface2'}`}>
        <span className={`absolute top-0.5 size-5 rounded-full bg-ink transition-all ${on ? 'left-[1.375rem]' : 'left-0.5'}`} />
      </button>
    </div>
  );
}
